import { useEffect, useState, useMemo, useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { COOKIES } from '../constants';
import { decrypt, encrypt } from '../utils/crypt';
import { currentUserQuery } from '../gql/user';

const useUser = ({ token, cookie }) => {
  const cookieName = COOKIES.LS_USER;
  const [user, setUser] = useState();
  const [loading, setLoading] = useState();
  const [error, setError] = useState();

  const { getCookie, setCookie } = cookie || {};
  const userCookie = getCookie(cookieName) || '';

  const client = useApolloClient();

  const { displayName = '', account, suburb } = user || {};
  const { firstName = '', lastName = '', email = '', phoneNumber = '' } =
    account || {};

  const {
    id: suburbId = '',
    name: suburbName = '',
    state: suburbState = '',
    postcode: suburbPostcode = '',
    market = '',
    region = '',
  } = suburb || {};

  const getUser = async () => {
    setLoading(true);
    const { data: { currentUser } = {}, error: resError } = await client.query({
      query: currentUserQuery,
    });
    setUser(currentUser);
    setError(resError);
    setLoading(false);
  };

  const setUserCookie = async params => {
    await setCookie(
      cookieName,
      encrypt({
        displayName: params.name,
        email: params.email,
      }),
      {
        maxAge: 60 * 60 * 24 * 1,
      }
    );
  };

  useEffect(() => {
    if (token) {
      getUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const isStaff = useMemo(() => {
    const { userRoles: { edges = [] } = {} } = user || {};
    return !!edges.some(({ node }) => node.roleName === 'staff');
  }, [user]);

  const isPersoniv = useMemo(() => {
    const { userRoles: { edges = [] } = {} } = user || {};
    return !!edges.some(({ node }) => node.roleName === 'personiv');
  }, [user]);

  const hasListingRole = useCallback(
    listingId => {
      const { userRoles: { edges = [] } = {} } = user || {};

      try {
        const ids = edges.reduce(
          (acc, { node }) =>
            node.roleName.includes('L_')
              ? [...acc, node.roleName.replace('L_', '')]
              : acc,
          []
        );
        if (ids.includes(`${listingId}`)) return true;
        // eslint-disable-next-line no-empty
      } catch (e) {
        return false;
      }
      return false;
    },
    [user]
  );

  const userContactDetails = useMemo(() => {
    if ((displayName || (firstName && lastName)) && email) {
      return {
        name: displayName || `${firstName} ${lastName}`,
        email,
        phone: phoneNumber,
        userSuburb: {
          suburbId,
          suburbName,
          suburbState,
          suburbPostcode,
          market,
          region,
        },
      };
    }
    if (userCookie && typeof userCookie === 'string') {
      const userCookieDetails = decrypt(userCookie);
      if (userCookieDetails.displayName && userCookieDetails.email)
        return {
          name: userCookieDetails.displayName,
          email: userCookieDetails.email,
        };
    }
    return {};
  }, [
    userCookie,
    displayName,
    lastName,
    firstName,
    email,
    phoneNumber,
    suburbId,
    suburbName,
    suburbState,
  ]);

  return {
    user,
    loading,
    error,
    isStaff,
    isPersoniv,
    hasListingRole,
    userContactDetails,
    setUserCookie,
  };
};

export default useUser;
